import { useState, useCallback, useRef, useEffect } from 'react';

const DEFAULT_HF_MODEL = process.env.REACT_APP_HUGGINGFACE_MODEL_ID || 'swiss-ai/Apertus-8B-Instruct-2509:fastest';
const SUGGESTED_HF_MODEL = 'katanemo/Arch-Router-1.5B:fastest';
const HF_CHAT_ENDPOINT = process.env.REACT_APP_HUGGINGFACE_ENDPOINT_URL || 'https://router.huggingface.co/v1/chat/completions';
const HF_STORAGE_KEY = 'codelves.huggingface.config';
const CUSTOM_STORAGE_KEY = 'codelves.custom-model.config';
const LOCAL_CUSTOM_MODEL_ID = 'local-browser-custom';
const LOCAL_MODEL_STORAGE_KEY = 'codelves.local-model.config';

const AVAILABLE_MODELS = [
  {
    id: 'HuggingFaceTB/SmolLM2-135M-Instruct',
    provider: 'local',
    name: 'SmolLM2 135M',
    size: '~135M params',
    description: 'Same in-browser model family as Workshop',
  },
  {
    id: 'HuggingFaceTB/SmolLM2-360M-Instruct',
    provider: 'local',
    name: 'SmolLM2 360M',
    size: '~360M params',
    description: 'Larger local model with the same worker path',
  },
  {
    id: 'Xenova/Qwen2.5-0.5B-Instruct',
    provider: 'local',
    name: 'Qwen2 0.5B',
    size: '~0.5B params',
    description: 'Stronger local instruct model for the browser',
  },
  {
    id: LOCAL_CUSTOM_MODEL_ID,
    provider: 'local',
    name: 'Custom Local Model',
    description: 'Paste a model URL or repo id to try in-browser',
    configurable: true,
  },
  {
    id: 'huggingface-browser',
    provider: 'huggingface',
    name: 'Hugging Face',
    description: 'Uses your own HF token in this browser',
    configurable: true,
  },
  {
    id: 'custom-browser',
    provider: 'custom',
    name: 'Custom Endpoint',
    description: 'OpenAI-compatible endpoint with your own token',
    configurable: true,
  },
];

const DEFAULT_LOCAL_MODEL_ID = 'HuggingFaceTB/SmolLM2-135M-Instruct';

const LEGACY_DEFAULT_HF_MODELS = new Set([
  'deepseek-ai/DeepSeek-R1',
  'deepseek-ai/DeepSeek-R1:fastest',
  'deepseek-ai/DeepSeek-R1:hf-inference',
  'Qwen/Qwen3-Coder-480B-A35B-Instruct',
  'Qwen/Qwen3-Coder-480B-A35B-Instruct:fastest',
  'Qwen/Qwen3-Coder-480B-A35B-Instruct:hf-inference',
]);

function canUseStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function readStoredConfig(key) {
  if (!canUseStorage()) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeStoredConfig(key, value) {
  if (!canUseStorage()) {
    return;
  }
  window.localStorage.setItem(key, JSON.stringify(value));
}

function isHttpUrl(value) {
  return /^https?:\/\//i.test((value || '').trim());
}

function tryParseUrl(value) {
  try {
    return new URL(value);
  } catch {
    return null;
  }
}

function looksLikeModelId(value) {
  const trimmed = (value || '').trim();
  return Boolean(trimmed) && trimmed.includes('/') && !trimmed.includes(' ') && !isHttpUrl(trimmed);
}

function normalizeLocalModelSource(value) {
  const trimmed = (value || '').trim();
  if (!trimmed) {
    return '';
  }

  const parsedUrl = tryParseUrl(trimmed);
  if (parsedUrl && parsedUrl.hostname.includes('huggingface.co')) {
    const pathParts = parsedUrl.pathname.split('/').filter(Boolean);
    if (pathParts.length >= 2) {
      return `${pathParts[0]}/${pathParts[1]}`;
    }
  }

  return trimmed;
}

function normalizeRouterModelId(modelId) {
  const trimmed = (modelId || '').trim();
  if (!trimmed) {
    return DEFAULT_HF_MODEL;
  }
  if (LEGACY_DEFAULT_HF_MODELS.has(trimmed)) {
    return DEFAULT_HF_MODEL;
  }
  if (trimmed.endsWith(':hf-inference')) {
    return `${trimmed.slice(0, -':hf-inference'.length)}:fastest`;
  }
  return trimmed.includes(':') ? trimmed : `${trimmed}:fastest`;
}

function getStoredHuggingFaceConfig() {
  const config = readStoredConfig(HF_STORAGE_KEY);
  if (!config) {
    return null;
  }

  const normalizedModelId = normalizeRouterModelId(config.modelId);
  if (normalizedModelId !== config.modelId) {
    const nextConfig = {
      ...config,
      modelId: normalizedModelId,
      endpointUrl: config.endpointUrl || HF_CHAT_ENDPOINT,
    };
    writeStoredConfig(HF_STORAGE_KEY, nextConfig);
    return nextConfig;
  }

  return config;
}

function getStoredLocalModelConfig() {
  const config = readStoredConfig(LOCAL_MODEL_STORAGE_KEY);
  if (!config) {
    return null;
  }

  const normalizedModelId = normalizeLocalModelSource(config.modelId || config.source);
  if (!normalizedModelId) {
    return null;
  }

  if (normalizedModelId !== config.modelId) {
    const nextConfig = {
      ...config,
      modelId: normalizedModelId,
    };
    writeStoredConfig(LOCAL_MODEL_STORAGE_KEY, nextConfig);
    return nextConfig;
  }

  return config;
}

function humanizeModelError(message) {
  const text = `${message || ''}`.trim();
  const lower = text.toLowerCase();

  if (
    lower.includes('inference providers') ||
    lower.includes('insufficient permissions') ||
    lower.includes('invalid user token') ||
    (lower.includes('token') && (lower.includes('401') || lower.includes('403') || lower.includes('unauthorized')))
  ) {
    return 'Hugging Face rejected the token. Create a User Access Token with the "Inference Providers" permission, then paste it again.';
  }

  if (
    (lower.includes('model') &&
      (lower.includes('not found') || lower.includes('404') || lower.includes('does not exist')))
  ) {
    return 'That hosted model could not be found. Use a Hugging Face router model id like deepseek-ai/DeepSeek-R1:fastest, or a DeepSeek API model like deepseek-reasoner with the DeepSeek API endpoint.';
  }

  if (
    lower.includes('model_quantized.onnx') ||
    (lower.includes('could not locate file') && lower.includes('/onnx/'))
  ) {
    return 'That local model is not laid out the way this browser worker expects. Try the built-in SmolLM or Qwen options, or use a transformers.js-compatible Hugging Face repo.';
  }

  if (
    lower.includes('could not load @huggingface/transformers') ||
    lower.includes('failed to fetch dynamically imported module') ||
    lower.includes('importing runtime')
  ) {
    return 'The browser could not load the local model runtime. Refresh once and try again.';
  }

  return text || 'Something went wrong while loading the model.';
}

export default function useTransformers() {
  const [modelLoaded, setModelLoaded] = useState(false);
  const [modelLoading, setModelLoading] = useState(false);
  const [loadProgress, setLoadProgress] = useState(0);
  const [selectedModel, setSelectedModel] = useState(AVAILABLE_MODELS[0].id);
  const [activeModel, setActiveModel] = useState(null);
  const [localBackend, setLocalBackend] = useState(null);
  const [error, setError] = useState(null);
  const [huggingFaceTokenConfigured, setHuggingFaceTokenConfigured] = useState(Boolean(getStoredHuggingFaceConfig()?.token));
  const [customModelConfigured, setCustomModelConfigured] = useState(Boolean(readStoredConfig(CUSTOM_STORAGE_KEY)?.endpointUrl));
  const [localCustomModelConfigured, setLocalCustomModelConfigured] = useState(Boolean(getStoredLocalModelConfig()?.modelId));
  const workerRef = useRef(null);
  const pendingResolve = useRef(null);
  const loadedLocalModelRef = useRef(null);
  const loadedLocalSourceRef = useRef(null);
  const pendingLocalSelectionRef = useRef(null);
  const hasAttemptedAutoLoadRef = useRef(false);

  useEffect(() => {
    const worker = new Worker(
      new URL('../workers/transformers.worker.js', import.meta.url),
      { type: 'module' }
    );

    worker.onmessage = (e) => {
      const { type, payload } = e.data;

      switch (type) {
        case 'progress':
          setLoadProgress(payload.progress || 0);
          break;
        case 'loaded': {
          const pendingLocal = pendingLocalSelectionRef.current;
          const resolvedButtonId = pendingLocal?.actualModelId === payload.modelId
            ? pendingLocal.buttonId
            : payload.modelId;
          const nextModel = AVAILABLE_MODELS.find((model) => model.id === resolvedButtonId)
            || AVAILABLE_MODELS.find((model) => model.id === payload.modelId)
            || null;
          loadedLocalModelRef.current = resolvedButtonId;
          loadedLocalSourceRef.current = payload.modelId;
          pendingLocalSelectionRef.current = null;
          setSelectedModel(resolvedButtonId);
          setActiveModel(nextModel);
          setLocalBackend(payload.backend || null);
          setModelLoaded(true);
          setModelLoading(false);
          setError(null);
          break;
        }
        case 'error':
          setError(humanizeModelError(payload.message));
          setModelLoaded(false);
          setModelLoading(false);
          setLocalBackend(null);
          loadedLocalModelRef.current = null;
          loadedLocalSourceRef.current = null;
          pendingLocalSelectionRef.current = null;
          if (pendingResolve.current) {
            pendingResolve.current(`Error: ${humanizeModelError(payload.message)}`);
            pendingResolve.current = null;
          }
          break;
        case 'result':
          if (pendingResolve.current) {
            pendingResolve.current(payload.error ? `Error: ${humanizeModelError(payload.error)}` : payload.text);
            pendingResolve.current = null;
          }
          break;
        default:
          break;
      }
    };

    // If the worker itself crashes (e.g. it can't import @huggingface/transformers
    // because of a CORS-blocked WASM file) we must surface the failure — otherwise
    // modelLoading stays true forever and the UI just spins.
    worker.onerror = (event) => {
      const message = event?.message || event?.error?.message || 'The local model worker crashed before it could load the model.';
      setError(humanizeModelError(message));
      setModelLoaded(false);
      setModelLoading(false);
      setLocalBackend(null);
      loadedLocalModelRef.current = null;
      loadedLocalSourceRef.current = null;
      pendingLocalSelectionRef.current = null;
      if (pendingResolve.current) {
        pendingResolve.current(`Error: ${humanizeModelError(message)}`);
        pendingResolve.current = null;
      }
    };
    worker.onmessageerror = () => {
      setError('The local model worker sent an unreadable message. Try reloading the workspace.');
      setModelLoaded(false);
      setModelLoading(false);
      setLocalBackend(null);
      loadedLocalModelRef.current = null;
      loadedLocalSourceRef.current = null;
    };

    workerRef.current = worker;
    return () => worker.terminate();
  }, []);

  const activateRemoteModel = useCallback((model) => {
    setSelectedModel(model.id);
    setActiveModel(model);
    setLocalBackend(null);
    setModelLoaded(true);
    setModelLoading(false);
    setLoadProgress(100);
    setError(null);
  }, []);

  const configureLocalModel = useCallback(() => {
    const existing = getStoredLocalModelConfig();
    const source = window.prompt(
      'Paste a transformers.js-compatible Hugging Face model URL or repo id. Hugging Face model page URLs are okay.',
      existing?.source || existing?.modelId || ''
    );

    if (source === null) {
      return null;
    }

    const modelId = normalizeLocalModelSource(source);
    if (!modelId) {
      throw new Error('A model URL or repo id is required for that option.');
    }

    const nextConfig = {
      source: source.trim(),
      modelId,
    };

    writeStoredConfig(LOCAL_MODEL_STORAGE_KEY, nextConfig);
    setLocalCustomModelConfigured(true);
    return nextConfig;
  }, []);

  const configureHuggingFaceModel = useCallback(() => {
    const existing = getStoredHuggingFaceConfig();
    const token = window.prompt(
      'Paste a Hugging Face User Access Token with Inference Providers permission.',
      existing?.token || ''
    );

    if (token === null) {
      return null;
    }

    if (!token.trim()) {
      throw new Error('A Hugging Face token is required for that option.');
    }

    const modelId = window.prompt(
      'Optional: enter a Hugging Face router model id. Suggested example: katanemo/Arch-Router-1.5B or katanemo/Arch-Router-1.5B:fastest. Leave it blank to use the default hosted model.',
      existing?.modelId || SUGGESTED_HF_MODEL
    );

    if (modelId === null) {
      return null;
    }

    const nextConfig = {
      token: token.trim(),
      modelId: normalizeRouterModelId(modelId),
      endpointUrl: HF_CHAT_ENDPOINT,
    };

    writeStoredConfig(HF_STORAGE_KEY, nextConfig);
    setHuggingFaceTokenConfigured(true);
    return nextConfig;
  }, []);

  const configureCustomModel = useCallback(() => {
    const existing = readStoredConfig(CUSTOM_STORAGE_KEY);
    const existingEntry = existing?.endpointUrl === HF_CHAT_ENDPOINT && existing?.modelName
      ? existing.modelName
      : existing?.endpointUrl || 'https://generativelanguage.googleapis.com/v1beta/openai/chat/completions';
    const endpointOrModel = window.prompt(
      'Paste an OpenAI-compatible chat completions endpoint URL. You can also paste a Hugging Face model id like deepseek-ai/DeepSeek-R1 as shorthand.',
      existingEntry
    );

    if (endpointOrModel === null) {
      return null;
    }

    if (!endpointOrModel.trim()) {
      throw new Error('A custom endpoint URL or Hugging Face model id is required for that option.');
    }

    const bearerToken = window.prompt(
      'Optional: paste a bearer token for that endpoint.',
      existing?.bearerToken || ''
    );

    if (bearerToken === null) {
      return null;
    }

    if (looksLikeModelId(endpointOrModel)) {
      const nextConfig = {
        endpointUrl: HF_CHAT_ENDPOINT,
        modelName: normalizeRouterModelId(endpointOrModel),
        bearerToken: bearerToken.trim(),
      };

      writeStoredConfig(CUSTOM_STORAGE_KEY, nextConfig);
      setCustomModelConfigured(true);
      return nextConfig;
    }

    if (!isHttpUrl(endpointOrModel)) {
      throw new Error('That does not look like a valid endpoint URL. If you meant a Hugging Face model, use a value like deepseek-ai/DeepSeek-R1.');
    }

    const modelName = window.prompt(
      'Optional: enter the model name to send to that endpoint. For DeepSeek API this would usually be deepseek-chat or deepseek-reasoner.',
      existing?.modelName || 'default'
    );

    if (modelName === null) {
      return null;
    }

    const nextConfig = {
      endpointUrl: endpointOrModel.trim(),
      modelName: (modelName || 'default').trim() || 'default',
      bearerToken: bearerToken.trim(),
    };

    writeStoredConfig(CUSTOM_STORAGE_KEY, nextConfig);
    setCustomModelConfigured(true);
    return nextConfig;
  }, []);

  const loadModel = useCallback((modelId, options = {}) => {
    const model = AVAILABLE_MODELS.find((item) => item.id === modelId);
    if (!model) {
      return;
    }

    setError(null);
    setLoadProgress(0);

      if (model.provider === 'local') {
        if (!workerRef.current) {
          setError('The model worker is not ready yet.');
          return;
        }

      let actualModelId = model.id;
      if (model.id === LOCAL_CUSTOM_MODEL_ID) {
        const existingConfig = getStoredLocalModelConfig();
        const shouldEditConfig = Boolean(options.edit) || !existingConfig;
        const config = shouldEditConfig ? configureLocalModel() : existingConfig;
        if (!config) {
          return;
        }
        actualModelId = config.modelId;
      }

      if (loadedLocalModelRef.current === model.id && loadedLocalSourceRef.current === actualModelId) {
        setSelectedModel(model.id);
        setActiveModel(model);
        setModelLoaded(true);
        setModelLoading(false);
        return;
      }

      setModelLoading(true);
      setModelLoaded(false);
      setLocalBackend(null);
      pendingLocalSelectionRef.current = { buttonId: model.id, actualModelId };
      workerRef.current.postMessage({ type: 'load', payload: { modelId: actualModelId } });
      return;
    }

    try {
      if (model.provider === 'huggingface') {
        const existingConfig = getStoredHuggingFaceConfig();
        const shouldEditConfig = Boolean(options.edit) || !existingConfig;
        const config = shouldEditConfig ? configureHuggingFaceModel() : existingConfig;
        if (!config) {
          return;
        }
        activateRemoteModel(model);
        return;
      }

      if (model.provider === 'custom') {
        const existingConfig = readStoredConfig(CUSTOM_STORAGE_KEY);
        const shouldEditConfig = Boolean(options.edit) || !existingConfig;
        const config = shouldEditConfig ? configureCustomModel() : existingConfig;
        if (!config) {
          return;
        }
        activateRemoteModel(model);
      }
    } catch (err) {
      setError(humanizeModelError(err.message));
    }
  }, [activateRemoteModel, configureCustomModel, configureHuggingFaceModel, configureLocalModel]);

  const generate = useCallback((prompt, opts = {}) => {
    return new Promise((resolve) => {
      const model = AVAILABLE_MODELS.find((item) => item.id === selectedModel);

      if (!workerRef.current || !model || !modelLoaded) {
        resolve(null);
        return;
      }

      pendingResolve.current = resolve;

      if (model.provider === 'local') {
        workerRef.current.postMessage({ type: 'generate', payload: { prompt, options: opts } });
        return;
      }

      if (model.provider === 'huggingface') {
        const config = getStoredHuggingFaceConfig();
        if (!config?.token) {
          resolve('Error: Hugging Face is not configured in this browser.');
          pendingResolve.current = null;
          return;
        }

        workerRef.current.postMessage({
          type: 'remote-generate',
          payload: {
            provider: 'huggingface',
            prompt,
            options: opts,
            endpointUrl: config.endpointUrl || HF_CHAT_ENDPOINT,
            modelId: normalizeRouterModelId(config.modelId),
            bearerToken: config.token,
          },
        });
        return;
      }

      if (model.provider === 'custom') {
        const config = readStoredConfig(CUSTOM_STORAGE_KEY);
        if (!config?.endpointUrl) {
          resolve('Error: The custom endpoint is not configured in this browser.');
          pendingResolve.current = null;
          return;
        }

        workerRef.current.postMessage({
          type: 'remote-generate',
          payload: {
            provider: 'custom',
            prompt,
            options: opts,
            endpointUrl: config.endpointUrl,
            modelName: config.modelName,
            bearerToken: config.bearerToken,
          },
        });
        return;
      }

      resolve(null);
      pendingResolve.current = null;
    });
  }, [modelLoaded, selectedModel]);

  useEffect(() => {
    if (hasAttemptedAutoLoadRef.current || modelLoading || modelLoaded) {
      return;
    }

    hasAttemptedAutoLoadRef.current = true;
    loadModel(DEFAULT_LOCAL_MODEL_ID);
  }, [loadModel, modelLoaded, modelLoading]);

  return {
    modelLoaded,
    modelLoading,
    loadProgress,
    selectedModel,
    availableModels: AVAILABLE_MODELS,
    activeModel,
    localBackend,
    error,
    loadModel,
    editModel: (modelId) => loadModel(modelId, { edit: true }),
    generate,
    clearError: useCallback(() => setError(null), []),
    huggingFaceTokenConfigured,
    customModelConfigured,
    localCustomModelConfigured,
  };
}
